import { isAllowedRedirect } from '@sdc/auth';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Splash } from '../components/Splash';
import { SynthwaveBg } from '../components/SynthwaveBg';
import { logoColor } from '../lib/logos';
import { authLink, describeAuthError, linkNeedsPassword } from '../lib/authLink';
import { logAuditEvent } from '../lib/audit';
import { consumeAuthTokens } from '../lib/consumeAuthTokens';
import { useSessionWait } from '../lib/useSessionWait';

/**
 * Landeseite fuer Invite-, Recovery- und Magic-Link-Mails.
 *
 * supabase-js liest die Tokens aus der URL (`detectSessionInUrl`); die Parameter
 * selbst kommen aus `authLink`, das sie beim Modul-Laden einsammelt, bevor der
 * Client sie wegraeumt.
 */
export function AuthCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { state } = useSessionWait();
  const handled = useRef(false);
  const [exchangeError, setExchangeError] = useState<string | null>(null);

  // Tokens aus dem Link gegen eine Session tauschen.
  useEffect(() => {
    void consumeAuthTokens().then(({ error }) => {
      if (error) setExchangeError(error);
    });
  }, []);

  const linkError = describeAuthError() ?? exchangeError;

  useEffect(() => {
    if (linkError || state !== 'ready' || handled.current) return;
    handled.current = true;

    // Fire and forget: das Audit-Log darf die Weiterleitung nicht aufhalten.
    void logAuditEvent('login', { method: authLink.type ?? 'magic_link' });

    if (linkNeedsPassword) {
      navigate('/set-password', { replace: true });
      return;
    }

    const redirect = params.get('redirect');
    if (redirect && isAllowedRedirect(redirect)) {
      window.location.replace(redirect);
      return;
    }
    const next = params.get('next');
    navigate(next && next.startsWith('/') && !next.startsWith('//') ? next : '/', { replace: true });
  }, [state, linkError, navigate, params]);

  if (linkError || state === 'none') {
    return (
      <>
        <SynthwaveBg />
        <div className="auth">
          <div className="auth__card">
          <div className="auth__brand">
            <img src={logoColor} alt="" width={42} height={42} />
            <span>
              SDC <em>HUB</em>
            </span>
          </div>
          <h1>Anmeldung nicht abgeschlossen</h1>
          <div className="alert alert--error" role="alert">
            {linkError ?? 'Der Link hat keine Session erzeugt. Meist ist er abgelaufen oder wurde schon einmal geöffnet.'}
          </div>
          <p className="card__meta">
            Fordere auf der Login-Seite einen neuen Link an – bei „Magic Link" oder über „Passwort
            vergessen?". Jeder Link gilt 60 Minuten und nur einmal.
          </p>
          <Link to="/login" replace className="px-btn px-btn--block">
            Zum Login
          </Link>
          <p className="auth__hint">
            Technische Details: type={authLink.type ?? '–'}, tokens=
            {authLink.hasTokens ? 'ja' : 'nein'}, code={authLink.hasCode ? 'ja' : 'nein'}
            {authLink.errorCode ? `, code=${authLink.errorCode}` : ''}
          </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SynthwaveBg />
      <Splash label="Anmeldung wird abgeschlossen …" />
    </>
  );
}
